import { FC, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useQueryClient } from 'react-query';
import {
  roomErrorKind,
  roomErrorMessage,
} from '@/screens/guest/lib/room-errors';
import { NotConcludedRoomsResponse } from '../types';
import { useHttpMutation, useHttpQuery } from '../../../api';
import { API_URLS } from '../consts';
import { Link, useNavigate } from 'react-router-dom';
import {
  BhabiLink,
  LoadingLayout,
  UserAvatar,
  useAlert,
} from '../../../shared/components';
import { BhabiStorage } from '@/shared/lib';
import { getUser } from '../../../redux/reducers';
import { IN_PLAY_ROUTE } from '../in-play';
import { LoggerService } from '../../../shared/logger';
import { RotateCw } from 'lucide-react';
import { roomStatus } from './room-status';
import { Capacitor } from '@capacitor/core';
import {
  ROOMS_GRID_DESKTOP_QUERY,
  ROOMS_GRID_TABLET_QUERY,
  roomGridColumns,
  roomGridPageSize,
  roomListClassName,
  visibleRoomPlayers,
} from './room-players';
import './social.css';
import { useRandomColor } from '../in-play/components/playground';
import { CREATE_ROOM_ROUTE } from '../create-room/route.definitions';
import {
  PROFILE_ROUTE,
  PUBLIC_PROFILE_ROUTE,
} from '../profile/route.definitions';
import {
  BhabiPagination,
  InvitePlayerPageLayout,
  PaginationSlotContext,
  usePaginationHelper,
} from '../invite-players/components';
import { dispatch } from '@/redux';

// ~196px per room card (head + avatar/name row + error slot + 44px button)
// ~186px total chrome (98px topbar/tabs + ~72px pagination footer + gaps)
const ROOMS_ROW_HEIGHT = 196;
const ROOMS_CHROME_PX = 186;
// Web grid (>=768, see social.css): a grid row is a wrapped chip block +
// list padding/gap, so budget a taller row and a little extra chrome.
const ROOMS_GRID_ROW_HEIGHT = 252;
const ROOMS_GRID_CHROME_PX = ROOMS_CHROME_PX + 24;

export const UserRooms: FC = () => {
  const { data, isLoading, isError, refetch, isFetching } = useHttpQuery<
    NotConcludedRoomsResponse[]
  >('notConcludedRooms', API_URLS.NOT_CONCLUDED_ROOMS, 'GET');

  const { mutate: reJoinRoom } = useHttpMutation<
    { token: string },
    { roomID: string }
  >(API_URLS.RE_JOIN_ROOM, 'POST');

  const nav = useNavigate();
  const { addAlert } = useAlert();
  const queryClient = useQueryClient();
  const [rejoining, setRejoining] = useState<string | null>(null);
  const [rejoinError, setRejoinError] = useState<{
    roomID: string;
    message: string;
  } | null>(null);

  const onHandleReJoinRoom = (roomID: string) => () => {
    if (rejoining) return; // one rejoin at a time; ignore double taps
    setRejoining(roomID);
    setRejoinError(null);
    reJoinRoom(
      { roomID },
      {
        onSuccess: res => {
          BhabiStorage.setToken(res.token);
          dispatch(getUser(res.token));
          nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
        },
        onError: err => {
          LoggerService.error('Re-join room failed');
          const message = roomErrorMessage(
            err,
            "Couldn't rejoin this table. Try again."
          );
          setRejoining(null);
          setRejoinError({ roomID, message });
          addAlert({ type: 'error', message });
          // a finished / closed table should drop out of the list
          if (roomErrorKind(err) !== 'network') {
            queryClient.invalidateQueries('notConcludedRooms');
          }
        },
      }
    );
  };

  const native = Capacitor.isNativePlatform();
  const tablet = useMediaQuery(ROOMS_GRID_TABLET_QUERY);
  const desktop = useMediaQuery(ROOMS_GRID_DESKTOP_QUERY);
  const columns = roomGridColumns({ native, tablet, desktop });
  const grid = columns > 1;

  const {
    page,
    setPage,
    paginatedData,
    setPageSize,
    pageSize,
    initialPageSizeValue,
  } = usePaginationHelper(
    grid ? ROOMS_GRID_CHROME_PX : ROOMS_CHROME_PX,
    // N columns fit N cards per row of height H → H/N per card.
    grid ? ROOMS_GRID_ROW_HEIGHT / columns : ROOMS_ROW_HEIGHT,
    data
  );

  // Web grid only: snap to whole rows. Runs after the helper's own sync
  // effect, so this wins; single column (mobile/native) is never touched.
  const gridPageSize = roomGridPageSize(initialPageSizeValue, columns);
  useEffect(() => {
    if (grid && pageSize !== gridPageSize) setPageSize(gridPageSize);
  }, [grid, gridPageSize, pageSize, setPageSize]);

  const totalItems = data?.length ?? 0;
  const showPagination = totalItems > pageSize;

  const setPaginationContent = useContext(PaginationSlotContext);

  useEffect(() => {
    if (!setPaginationContent) return;
    setPaginationContent(
      showPagination ? (
        <BhabiPagination
          page={page}
          setPage={setPage}
          totalItems={totalItems}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={[]}
        />
      ) : null
    );
    return () => setPaginationContent(null);
  }, [
    setPaginationContent,
    showPagination,
    page,
    setPage,
    totalItems,
    pageSize,
    setPageSize,
  ]);

  if (isLoading && !isError) {
    return <LoadingLayout inline text="Looking for your tables…" />;
  }

  if (isError) {
    return (
      <div className="bh-list-state" role="alert">
        <p className="bh-h3">Couldn&apos;t load your tables</p>
        <p className="bh-muted bh-small">
          Check your connection and try again.
        </p>
        <button
          type="button"
          className="bh-btn bh-btn--secondary bh-btn--small"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-busy={isFetching}
        >
          <RotateCw size={16} aria-hidden="true" />
          {isFetching ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    );
  }

  if (!paginatedData?.length) {
    return (
      <div className="bh-state" style={{ minHeight: 240 }}>
        <div className="bh-state__inner">
          <p className="bh-h3">No tables waiting for you</p>
          <p className="bh-muted bh-small" style={{ margin: 0 }}>
            Tables you leave mid-game show up here so you can jump back in.
          </p>
          <Link
            to={CREATE_ROOM_ROUTE.ABSOLUTE_PATH}
            className="bh-btn bh-btn--primary bh-btn--small"
          >
            Create a table
          </Link>
        </div>
      </div>
    );
  }

  return (
    <InvitePlayerPageLayout>
      <ul
        className={roomListClassName(native)}
        aria-label="Tables waiting for you"
      >
        {paginatedData?.map((n, i) => {
          const status = roomStatus(n);
          const busy = rejoining === n.roomID;
          const errorId = `rejoin-error-${n.roomID}`;
          const hasError = rejoinError?.roomID === n.roomID;
          return (
            <li key={[n.roomID, i].join('-')} className="bh-room-card">
              <div className="bh-room-card__head">
                <div className="min-w-0">
                  <p className="bh-room-card__title">Table waiting for you</p>
                  <p className="bh-room-card__meta">
                    {status.seats}
                    {status.here > 0 && ` · ${status.here} here now`}
                  </p>
                </div>
                <span className="bh-room-card__tag" data-tone={status.tone}>
                  {status.tag}
                </span>
              </div>
              <UsersInRoomList list={n.users} />
              {hasError && (
                <p className="bh-room-card__error" role="alert" id={errorId}>
                  {rejoinError?.message}
                </p>
              )}
              <button
                type="button"
                className="bh-btn bh-btn--primary bh-btn--small bh-btn--block"
                disabled={!!rejoining}
                aria-busy={busy}
                aria-describedby={hasError ? errorId : undefined}
                onClick={onHandleReJoinRoom(n.roomID)}
              >
                {busy
                  ? 'Rejoining…'
                  : hasError
                    ? 'Try rejoining again'
                    : 'Rejoin table'}
              </button>
            </li>
          );
        })}
      </ul>
    </InvitePlayerPageLayout>
  );
};

export interface UsersInRoomListProps {
  list: {
    email: string;
    userID: string;
    name: string;
    photoURL: string;
    state: 'online' | 'offline' | 'joined' | 'left' | 'rejoined';
  }[];
}

export const UsersInRoomList: FC<UsersInRoomListProps> = ({ list }) => {
  const avatarColors = useRandomColor(list.length);
  const compact = useMediaQuery('(max-width:420px)');
  const { visible, remaining } = visibleRoomPlayers(list, compact);

  return (
    <ul
      className="bh-room-card__players"
      aria-label={`${list.length} players at this table`}
    >
      {visible.map((user, i) => (
        <li key={user.userID} className="bh-room-card__player">
          <BhabiLink
            aria-label={`${user.name}'s profile`}
            to={[
              PROFILE_ROUTE.ABSOLUTE_PATH,
              PUBLIC_PROFILE_ROUTE.KEY,
              `?user_id=${user.userID}`,
            ].join('/')}
            className="bh-room-card__avatar-link"
          >
            <UserAvatar
              {...{
                hideState: true,
                name: user.name,
                photoURL: user.photoURL,
                userState: user.state,
                avatarProps: {
                  sx: { bgcolor: avatarColors[i], width: 34, height: 34 },
                },
              }}
            />
          </BhabiLink>
          <span
            className="bh-room-card__name"
            title={user.name}
            aria-hidden="true"
          >
            {user.name}
          </span>
        </li>
      ))}
      {remaining > 0 && (
        <li
          className="bh-room-card__more"
          aria-label={`${remaining} more players`}
        >
          +{remaining}
        </li>
      )}
    </ul>
  );
};
